import SpaceBetween from '@cloudscape-design/components/space-between';
import FormField from '@cloudscape-design/components/form-field';
import Multiselect, { MultiselectProps } from '@cloudscape-design/components/multiselect';
import Input from '@cloudscape-design/components/input';
import DateRangePicker, { DateRangePickerProps } from '@cloudscape-design/components/date-range-picker';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Header from '@cloudscape-design/components/header';
import Button from '@cloudscape-design/components/button';
import { TicketFilters } from '../hooks/useTicketFilters';

interface FilterDrawerProps {
  filters: TicketFilters;
  onChange: <K extends keyof TicketFilters>(key: K, value: TicketFilters[K]) => void;
  onClear: () => void;
  onClose: () => void;
}

const STATUS_OPTIONS: MultiselectProps.Option[] = [
  { value: 'Pending', label: 'Pending' },
  { value: 'Assigned', label: 'Assigned' },
  { value: 'Work in Progress', label: 'Work in Progress' },
  { value: 'Researching', label: 'Researching' },
  { value: 'Resolved', label: 'Resolved' },
  { value: 'Closed', label: 'Closed' },
];

const SEVERITY_OPTIONS: MultiselectProps.Option[] = [
  { value: 'SEV_1', label: 'SEV 1' },
  { value: 'SEV_2', label: 'SEV 2' },
  { value: 'SEV_3', label: 'SEV 3' },
  { value: 'SEV_4', label: 'SEV 4' },
  { value: 'SEV_5', label: 'SEV 5' },
];

const DATE_RELATIVE_OPTIONS: DateRangePickerProps.RelativeOption[] = [
  { key: '7-days', amount: 7, unit: 'day', type: 'relative' },
  { key: '30-days', amount: 30, unit: 'day', type: 'relative' },
  { key: '90-days', amount: 90, unit: 'day', type: 'relative' },
  { key: '6-months', amount: 6, unit: 'month', type: 'relative' },
];

export function FilterDrawer({ filters, onChange, onClear, onClose }: FilterDrawerProps) {
  return (
    <SpaceBetween size="l">
      <Header
        variant="h3"
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClear}>Clear all</Button>
            <Button variant="icon" iconName="close" ariaLabel="Close filters" onClick={onClose} />
          </SpaceBetween>
        }
      >
        Filter tickets
      </Header>

      <FormField label="Status">
        <Multiselect
          selectedOptions={filters.statuses.map((s) => ({ value: s, label: s }))}
          options={STATUS_OPTIONS}
          placeholder="Choose one or more statuses"
          onChange={({ detail }) => onChange('statuses', detail.selectedOptions.map((o) => o.value!))}
          tokenLimit={2}
        />
      </FormField>

      <FormField label="Severity">
        <Multiselect
          selectedOptions={filters.severities.map((s) => ({ value: s, label: s.replace('_', ' ') }))}
          options={SEVERITY_OPTIONS}
          placeholder="Choose one or more severities"
          onChange={({ detail }) => onChange('severities', detail.selectedOptions.map((o) => o.value!))}
          tokenLimit={3}
        />
      </FormField>

      <FormField label="Original Severity">
        <Multiselect
          selectedOptions={filters.originalSeverities.map((s) => ({ value: s, label: s.replace('_', ' ') }))}
          options={SEVERITY_OPTIONS}
          placeholder="Choose one or more severities"
          onChange={({ detail }) => onChange('originalSeverities', detail.selectedOptions.map((o) => o.value!))}
          tokenLimit={3}
        />
      </FormField>

      <FormField label="Assignee">
        <Input
          value={filters.assignee === '__unassigned__' ? '' : filters.assignee}
          placeholder="Enter alias (e.g., marehal)"
          onChange={({ detail }) => onChange('assignee', detail.value)}
        />
      </FormField>

      <FormField label="Date Range">
        <DateRangePicker
          value={filters.dateRange}
          onChange={({ detail }) => onChange('dateRange', detail.value)}
          relativeOptions={DATE_RELATIVE_OPTIONS}
          isValidRange={() => ({ valid: true })}
          placeholder="Select date range"
          i18nStrings={{
            todayAriaLabel: 'Today',
            nextMonthAriaLabel: 'Next month',
            previousMonthAriaLabel: 'Previous month',
            customRelativeRangeDurationLabel: 'Duration',
            customRelativeRangeDurationPlaceholder: 'Enter duration',
            customRelativeRangeOptionLabel: 'Custom range',
            customRelativeRangeOptionDescription: 'Set a custom range in the past',
            customRelativeRangeUnitLabel: 'Unit of time',
            relativeModeTitle: 'Relative range',
            absoluteModeTitle: 'Absolute range',
            relativeRangeSelectionHeading: 'Choose a range',
            cancelButtonLabel: 'Cancel',
            clearButtonLabel: 'Clear',
            applyButtonLabel: 'Apply',
            formatRelativeRange: (e) => `Last ${e.amount} ${e.unit}${e.amount > 1 ? 's' : ''}`,
            formatUnit: (unit, value) => `${unit}${value > 1 ? 's' : ''}`,
            dateTimeConstraintText: '',
            startDateLabel: 'Start date',
            startTimeLabel: 'Start time',
            endDateLabel: 'End date',
            endTimeLabel: 'End time',
          }}
        />
      </FormField>

      <ExpandableSection headerText="Advanced filters">
        <SpaceBetween size="m">
          <FormField label="Tags" description="Comma-separated list of tags">
            <Input
              value=""
              placeholder="e.g., latency, auth, timeout"
              onChange={() => {}}
            />
          </FormField>
          <FormField label="Source System">
            <Input
              value=""
              placeholder="e.g., Default"
              onChange={() => {}}
            />
          </FormField>
        </SpaceBetween>
      </ExpandableSection>
    </SpaceBetween>
  );
}
